import { useEffect, useState } from 'react'

/**
 * The reader's own reply, remembered on this browser for a few minutes (2026-10-18).
 *
 * The review thread's reply box is commentHub's live widget: a reply shows at once there. Every
 * count around it — the feed row's "N replies", the thread's engagement row —
 * is DealHunt's `reply_count`, which trails commentHub by 1–3 minutes (worker ingest ~70 s,
 * DealHunt's memo 60 s, SoundGuys' read memo up to 5 min stale-while-revalidate). So the box
 * writes `{ noteId, total, at }` here when commentHub's total moves after the reader's own post,
 * and those counts show `max(reply_count, remembered)` until DealHunt catches up or the entry
 * expires (`OWN_REPLY_TTL_MS`).
 *
 * Client-only, read after mount (`useOwnReplyCount`): the feed and thread HTML are shared-cached
 * and always carry DealHunt's count. sessionStorage: the feed → thread → back path is one tab.
 */

export const OWN_REPLY_KEY = 'sg_community_own_replies'
/** Long enough to outlast DealHunt's lag (worst case ~8 min), short enough that a stale
 *  remembered count cannot hold a deleted reply up for long. */
export const OWN_REPLY_TTL_MS = 10 * 60_000
/** Window event after a write — the same page's other counts re-read (`storage` events do not
 *  fire in the tab that wrote). */
export const OWN_REPLY_EVENT = 'sg:own-reply'

export interface OwnReplyEntry {
  /** commentHub's thread total right after the reader's post. */
  total: number
  /** When it was written (ms). */
  at: number
}

export type OwnReplyMap = Record<string, OwnReplyEntry>

/** The entries still inside the TTL; anything malformed is dropped. Pure. */
export function pruneOwnReplies(value: unknown, now: number): OwnReplyMap {
  const out: OwnReplyMap = {}
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return out
  }
  Object.entries(value as Record<string, unknown>).forEach(([noteId, entry]) => {
    if (!entry || typeof entry !== 'object') {
      return
    }
    const { total, at } = entry as Record<string, unknown>
    if (
      typeof total === 'number' &&
      Number.isSafeInteger(total) &&
      total > 0 &&
      typeof at === 'number' &&
      at <= now &&
      now - at < OWN_REPLY_TTL_MS
    ) {
      out[noteId] = { total, at }
    }
  })
  return out
}

/** The count a surface shows: DealHunt's, raised to the remembered total. Pure. */
export function shownReplyCount(server: number | null, remembered: number | null): number | null {
  if (remembered === null || remembered <= 0) {
    return server
  }
  return Math.max(server ?? 0, remembered)
}

function readMap(now: number): OwnReplyMap {
  try {
    if (typeof window === 'undefined') {
      return {}
    }
    const raw = window.sessionStorage.getItem(OWN_REPLY_KEY)
    return pruneOwnReplies(raw ? JSON.parse(raw) : null, now)
  } catch {
    return {}
  }
}

/** The remembered total for this review, or null (none, expired, no storage, SSR). Prunes. */
export function ownReplyTotal(noteId: string, now: number = Date.now()): number | null {
  const map = readMap(now)
  try {
    window.sessionStorage.setItem(OWN_REPLY_KEY, JSON.stringify(map))
  } catch {
    // No storage: nothing to prune.
  }
  return map[noteId]?.total ?? null
}

/** The reply box, once commentHub's total reflects the reader's own post. */
export function rememberOwnReply(noteId: string, total: number, now: number = Date.now()): void {
  if (!noteId || !Number.isSafeInteger(total) || total <= 0) {
    return
  }
  const map = readMap(now)
  map[noteId] = { total, at: now }
  try {
    window.sessionStorage.setItem(OWN_REPLY_KEY, JSON.stringify(map))
    window.dispatchEvent(new CustomEvent(OWN_REPLY_EVENT, { detail: { noteId } }))
  } catch {
    // Private mode / quota: the counts wait for DealHunt, as before.
  }
}

/**
 * The reader's remembered total for a review: null on the server pass and the first client render
 * (identical HTML for every reader), then read after mount and whenever the box records a new one
 * on this page.
 */
export function useOwnReplyTotal(noteId: string): number | null {
  const [remembered, setRemembered] = useState<number | null>(null)
  useEffect(() => {
    const read = (): void => setRemembered(ownReplyTotal(noteId))
    read()
    window.addEventListener(OWN_REPLY_EVENT, read)
    return () => window.removeEventListener(OWN_REPLY_EVENT, read)
  }, [noteId])
  return remembered
}

/** DealHunt's `server` reply count, raised after mount to the reader's own remembered total. */
export function useOwnReplyCount(noteId: string, server: number | null): number | null {
  return shownReplyCount(server, useOwnReplyTotal(noteId))
}
